import { useState, useMemo } from "react";

import { cn } from "@/libs/utils";

import { ValueType } from "@/types/bid";

import CommonCheckbox from "@/components/Common/Checkbox";
import AccordionPanel from "@/components/AccordionPanel";
import CommonImg from "@/components/Common/Img";

import ArrowOpen from "@/assets/images/LNB/arrow-open.svg";
import ArrowClose from "@/assets/images/LNB/arrow-close.svg";

// TFSS에 속하는 디비전
const TFSS_DIVISIONS = ["Reagent", "Reagent_GSD", "LPD", "GSD", "BID", "BPG"];

type Props = {
  divisionList: ValueType[];
  selectedCompanyTypes: string[];
  selectedDivisions: string[];
  onChange: (companyTypes: string[], divisions: string[]) => void;
};

export default function DashboardCompanyDivisionFilter({
  divisionList,
  selectedCompanyTypes,
  selectedDivisions,
  onChange,
}: Props) {
  const [tfssOpen, setTfssOpen] = useState(false);
  const [tfskOpen, setTfskOpen] = useState(false);

  // TFSS 디비전 목록 (LPG, BID, CMD)
  const tfssDivisions = useMemo(
    () => divisionList.filter((d) => TFSS_DIVISIONS.includes(d.value)),
    [divisionList]
  );

  // TFSK 디비전 목록 (나머지, Others 제외)
  const tfskDivisions = useMemo(
    () => divisionList.filter((d) => !TFSS_DIVISIONS.includes(d.value) && d.value !== "Others"),
    [divisionList]
  );

  // 전체 선택 여부
  const isAllSelected =
    selectedCompanyTypes.includes("TFSS") &&
    selectedCompanyTypes.includes("TFSK") &&
    selectedCompanyTypes.includes("Others") &&
    divisionList.filter((d) => d.value !== "Others").every((d) => selectedDivisions.includes(d.value));

  // Others 선택 여부
  const isOthersSelected = selectedCompanyTypes.includes("Others");

  // TFSS 전체 선택 여부 (법인 + 디비전)
  const isTfssSelected =
    selectedCompanyTypes.includes("TFSS") &&
    tfssDivisions.every((d) => selectedDivisions.includes(d.value));

  // TFSK 전체 선택 여부 (법인 + 디비전)
  const isTfskSelected =
    selectedCompanyTypes.includes("TFSK") &&
    tfskDivisions.every((d) => selectedDivisions.includes(d.value));

  // 전체 선택/해제
  const handleAllChange = () => {
    if (isAllSelected) {
      onChange([], []);
    } else {
      onChange(["TFSS", "TFSK", "Others"], divisionList.filter((d) => d.value !== "Others").map((d) => d.value));
    }
  };

  // Others 선택/해제
  const handleOthersChange = () => {
    const newCompanyTypes = isOthersSelected
      ? selectedCompanyTypes.filter((t) => t !== "Others")
      : [...selectedCompanyTypes, "Others"];
    onChange(newCompanyTypes, selectedDivisions);
  };

  // TFSS 선택/해제 (1depth 클릭시 2depth도 전부 선택/해제)
  const handleTfssChange = () => {
    const newCompanyTypes = isTfssSelected
      ? selectedCompanyTypes.filter((t) => t !== "TFSS")
      : [...selectedCompanyTypes.filter((t) => t !== "TFSS"), "TFSS"];

    const tfssDivisionValues = tfssDivisions.map((d) => d.value);
    const newDivisions = isTfssSelected
      ? selectedDivisions.filter((d) => !tfssDivisionValues.includes(d))
      : [
          ...selectedDivisions.filter((d) => !tfssDivisionValues.includes(d)),
          ...tfssDivisionValues,
        ];

    onChange(newCompanyTypes, newDivisions);
  };

  // TFSK 선택/해제 (1depth 클릭시 2depth도 전부 선택/해제)
  const handleTfskChange = () => {
    const newCompanyTypes = isTfskSelected
      ? selectedCompanyTypes.filter((t) => t !== "TFSK")
      : [...selectedCompanyTypes.filter((t) => t !== "TFSK"), "TFSK"];

    const tfskDivisionValues = tfskDivisions.map((d) => d.value);
    const newDivisions = isTfskSelected
      ? selectedDivisions.filter((d) => !tfskDivisionValues.includes(d))
      : [
          ...selectedDivisions.filter((d) => !tfskDivisionValues.includes(d)),
          ...tfskDivisionValues,
        ];

    onChange(newCompanyTypes, newDivisions);
  };

  // 개별 디비전 선택/해제
  const handleDivisionChange = (
    divisionValue: string,
    parentType: "TFSS" | "TFSK"
  ) => {
    const isSelected = selectedDivisions.includes(divisionValue);
    const newDivisions = isSelected
      ? selectedDivisions.filter((d) => d !== divisionValue)
      : [...selectedDivisions, divisionValue];

    // 부모 법인의 모든 디비전이 선택되었는지 확인
    const parentDivisions =
      parentType === "TFSS" ? tfssDivisions : tfskDivisions;
    const allSelected = parentDivisions.every((d) =>
      newDivisions.includes(d.value)
    );

    // 디비전이 하나라도 선택되면 부모 법인도 선택, 아니면 해제
    const hasAnySelected = parentDivisions.some((d) =>
      newDivisions.includes(d.value)
    );

    let newCompanyTypes = selectedCompanyTypes;
    if (allSelected && !selectedCompanyTypes.includes(parentType)) {
      newCompanyTypes = [...selectedCompanyTypes, parentType];
    } else if (!hasAnySelected && selectedCompanyTypes.includes(parentType)) {
      newCompanyTypes = selectedCompanyTypes.filter((t) => t !== parentType);
    }

    onChange(newCompanyTypes, newDivisions);
  };

  return (
    <AccordionPanel title="자사 법인 구분">
      <div className="flex flex-col gap-2.5">
        {/* 전체 */}
        <CommonCheckbox
          label="전체"
          defaultValue={isAllSelected}
          onChange={handleAllChange}
        />

        {/* TFSS */}
        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            <CommonCheckbox
              label="TFSS"
              defaultValue={isTfssSelected}
              onChange={handleTfssChange}
            />
            {tfssDivisions.length > 0 && (
              <button
                type="button"
                onClick={() => setTfssOpen(!tfssOpen)}
                className="p-1"
              >
                <CommonImg
                  className="size-4"
                  src={tfssOpen ? ArrowClose : ArrowOpen}
                  alt={tfssOpen ? "Close" : "Open"}
                />
              </button>
            )}
          </div>
          {tfssOpen && tfssDivisions.length > 0 && (
            <div className="flex flex-col gap-2 pl-5 mt-2">
              {tfssDivisions.map((division) => (
                <CommonCheckbox
                  key={division.value}
                  label={division.label}
                  defaultValue={selectedDivisions.includes(division.value)}
                  onChange={() => handleDivisionChange(division.value, "TFSS")}
                />
              ))}
            </div>
          )}
        </div>

        {/* TFSK */}
        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            <CommonCheckbox
              label="TFSK"
              defaultValue={isTfskSelected}
              onChange={handleTfskChange}
            />
            {tfskDivisions.length > 0 && (
              <button
                type="button"
                onClick={() => setTfskOpen(!tfskOpen)}
                className="p-1"
              >
                <CommonImg
                  className="size-4"
                  src={tfskOpen ? ArrowClose : ArrowOpen}
                  alt={tfskOpen ? "Close" : "Open"}
                />
              </button>
            )}
          </div>
          {tfskOpen && tfskDivisions.length > 0 && (
            <div className="flex flex-col gap-2 pl-5 mt-2">
              {tfskDivisions.map((division) => (
                <CommonCheckbox
                  key={division.value}
                  label={division.label}
                  defaultValue={selectedDivisions.includes(division.value)}
                  onChange={() => handleDivisionChange(division.value, "TFSK")}
                />
              ))}
            </div>
          )}
        </div>

        {/* Others */}
        <CommonCheckbox
          label="Others"
          defaultValue={isOthersSelected}
          onChange={handleOthersChange}
        />
      </div>
    </AccordionPanel>
  );
}
